<template>
	<nta-page-layout>
		<template #default>
			<div style="width: 100%; height: 100%" class="d-flex justify-center align-center">
				<!-- Banner -->
				<AlternisBanner style="position: absolute; top: 50px; left: 0px" />

				<!-- LEFT -->
				<div style="width: 50%; height: 100%; overflow: hidden; display: flex; align-items: center">
					<AlternisGlobe :showLines="true" :showRegions="false" :showLabels="true" class="mt-10" />
				</div>

				<!-- RIGHT -->
				<div style="width: 50%; height: 100%">
					<div style="width: 100%; height: 100%" class="d-flex justify-center align-center">
						<!-- WORKSPACES -->
						<v-card color="contained" rounded="lg">
							<v-card-text>
								<v-list
									v-if="workspaces.length > 0"
									style="max-height: 40vh; overflow-y: auto; padding: 0"
									rounded="lg"
								>
									<v-list-item
										v-for="workspace in workspaces"
										:key="workspace.id"
										class="d-flex align-center"
										title="Voice Bot Call"
										:subtitle="new Date(workspace.timestamp).toLocaleString('de-DE')"
										prepend-icon="mdi-phone-in-talk"
										@click="workspaceClick(workspace.id)"
									>
										<template #append>
											<v-icon
												:style="{ visibility: workspace.clicked ? '' : 'hidden' }"
												color="green"
											>
												mdi-check
											</v-icon>
										</template>
									</v-list-item>
								</v-list>
								<v-alert v-else>
									<div>Noch keine neuen Calls eingetroffen.</div>
								</v-alert>
							</v-card-text>
						</v-card>
					</div>
				</div>

				<!-- Footer -->
				<div style="position: absolute; bottom: 0; left: 0">
					<PartnerLogo v-if="partner" :logoUrl="partner.logo" />
				</div>
				<div style="position: absolute; bottom: 0; right: 0">
					<PhoneCallQRCode v-if="partner" :id="partner.id" />
				</div>
			</div>
		</template>
	</nta-page-layout>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { api } from "@lr-notitia/notitia-operator/client";
import AlternisGlobe from "../components/AlternisGlobe.vue";
import AlternisBanner from "../components/AlternisBanner.vue";
import PartnerLogo from "../components/PartnerLogo.vue";
import PhoneCallQRCode from "../components/PhoneCallQRCode.vue";
import { theme } from "@lr-notitia/notitia-ui";

theme.vuetifyTheme.value = "dark";

interface Partner {
	id: string;
	name: string;
	domainId: string;
	instanceDataspaceId: string;
	color: string;
	logo: string;
}

const partner = ref<Partner>({
	id: "livereader",
	name: "LiveReader",
	domainId: "d0daff9f-2430-4785-bf00-104ab5aaca5d",
	instanceDataspaceId: isWorld()
		? "" // .world
		: "932576f5-b6bc-485b-a40b-b3f966cc007b", // .dev
	color: "#4db6ac",
	logo: "https://assets.livereader.com/asset/xXCbAJawnlM4BI9h.svg",
});

const workspaces = ref<
	{
		id: string;
		timestamp: number;
		clicked: boolean;
	}[]
>([]);

function workspaceClick(id: string) {
	if (!workspaces.value) return;

	const workspace = workspaces.value.find((w) => w.id === id);
	if (!workspace) return;
	workspace.clicked = true;

	const protocol = window.location.protocol; // http: or https:
	const hostname = window.location.hostname; // operator.notitia.dev or operator.notitia.world
	const url = `${protocol}//${hostname}/${partner.value.domainId}/template/${partner.value.instanceDataspaceId}/template/${id}`;
	window.open(url, "_blank");
}

function isWorld() {
	const hostname = window.location.hostname;
	return hostname.endsWith("world");
}

async function checkPopupBlockerStatus() {
	try {
		const testWindow = window.open("about:blank", "_blank");
		if (testWindow == null) {
			return "blocked";
		}
		testWindow.close();
		return "allowed";
	} catch (e) {
		return "blocked";
	}
}
onMounted(async () => {
	api()
		.socket()
		.domain(partner.value.domainId)
		.instance()
		.dataspace(partner.value.instanceDataspaceId)
		.on((message) => {
			const newId = message.workspaces[message.workspaces.length - 1];
			const newWorkspace = {
				id: newId,
				timestamp: Date.now(),
				clicked: false,
			};
			workspaces.value = [newWorkspace, ...workspaces.value];
			workspaceClick(newId);
		});
	const status = await checkPopupBlockerStatus();
	if (status === "blocked")
		alert(
			"Please allow popups for this site to enable automatic phone call opening in a new tab. If you don't do this, you can still click on the call in the list to open it manually. To allow popups, click on the icon in the address bar (left next to the URL) and click on 'site settings', then look for 'Pop-ups and redirects'."
		);
});
</script>

<style scoped lang="scss"></style>
